/**
 * The NZ patient invoicing dialog, on the settings shell the calendar and inventory use: a rail with the
 * patient, their balance and the sections, and a pane holding either the invoice list or the invoice editor.
 *
 * Laid out like a mail client: the patient's invoices as a list of cards down the left, and the one chosen
 * drawn as a printed invoice on the right, with a payment bar under it - payment is taken there, the way lens
 * dispensing takes it on its invoice card, so there is no separate payment screen to go to and come back from.
 *
 * Connecting to Xero is done here too, before the list is shown: logging in, choosing the organisation
 * when more than one is connected, and finding or creating the patient's Xero contact (after the lens
 * dispensing invoice card's lensXeroInvoicing). The patient header opens this dialog with a single button.
 *
 * The requests and the bodies they send are unchanged from the earlier dialog; only where things are
 * done from has moved. AU claiming has its own dialog (claimModalController) and is not touched here.
 */
app.controller("invoiceModalController", [
    "$scope", "accounts", "taxRates", "providerId", "CurrentPatient", "BillingAPI", "Session", "$window", "$filter", "$timeout", "REFERENCE_TYPE", "$mdDialog", "$q", "initialAction", "initialInvoiceId", "billingContactId", "InvoiceLiveUpdateService", "PracticeBillingDefaults", "PatientAPI", "InventoryAPI", "XeroItemCode",
    function ($scope, accounts, taxRates, providerId, CurrentPatient, BillingAPI, Session, $window, $filter, $timeout, REFERENCE_TYPE, $mdDialog, $q, initialAction, initialInvoiceId, billingContactId, InvoiceLiveUpdateService, PracticeBillingDefaults, PatientAPI, InventoryAPI, XeroItemCode) {
        // The practice's sales account and tax rate (Practice details), for an item that doesn't carry its own.
        let billingDefaults = PracticeBillingDefaults.fallback();
        PracticeBillingDefaults.load().then((d) => (billingDefaults = d));

        // The bank account last paid into at this practice is remembered in this browser, as lens dispensing does.
        const accountKey = () => "cattrax.invoicePaymentAccount." + ((Session.practice && Session.practice.id) || "");

        $scope.accounts = accounts || [];
        $scope.paymentAccounts = paymentAccountsOf($scope.accounts);
        $scope.taxRates = taxRates;
        $scope.providerId = providerId;
        $scope.$mdDialog = $mdDialog;

        // Opened to create an invoice: done once Xero is connected and the patient linked.
        let pendingCreate = false;

        // Opened on one invoice - a lens job's, from its tile: opened once the list has loaded, instead of the newest.
        let pendingInvoiceId = initialInvoiceId || null;

        // Stock of each inventory variation on the editor's lines, by "itemId#variationId", read once per item.
        let lineStock = {};

        // The invoice being written when the patient's Xero link went, kept to come back to once they are
        // linked again - see onLinkLost.
        let heldEditor = null;

        // Due-date shortcuts in the editor, counted from the clinic date.
        $scope.DUE_IN_DAYS = [7, 14, 30];

        // The most an item's price can be. A price typed over it is brought down to it on leaving the field.
        $scope.MAX_PRICE = 100000;

        $scope.clampPrice = function (value) {
            if (value === null || value === undefined || value === "" || isNaN(Number(value))) return value;
            return Math.min(Math.max(Number(value), 0), $scope.MAX_PRICE);
        };

        this.$onInit = function () {
            $scope.CP = CurrentPatient;
            $scope.BillingAPI = BillingAPI;
            $scope.REFERENCE_TYPE = REFERENCE_TYPE;
            $scope.billingContactId = billingContactId;

            // 'LIST', or the editor: 'CREATE' / 'EDIT'. Viewing and paying an invoice happen in the reading
            // pane beside the list, so they are no longer states of their own.
            $scope.viewState = "LIST";

            $scope.invoices = [];
            $scope.billingItems = [];
            $scope.isLoadingInvoices = false;
            $scope.hasLoaded = false;
            $scope.loadError = null;

            $scope.summary = emptySummary();
            // Feedback is given where an action happened, not in banners: `working` and `done` drive a
            // button's spinner and then its tick, `failed` an error beside what failed (kept until the next
            // try there), and `fresh` a brief highlight on what changed - a new or saved invoice's row, a
            // new payment's line.
            $scope.working = {};
            $scope.done = {};
            $scope.failed = {};
            $scope.fresh = {};

            // The invoice open in the reading pane: { invoiceId, invoice, message, confirm }. `invoice` is the
            // live row from the list, re-pointed whenever the list changes.
            $scope.selected = null;

            // The payment card's own copy of the invoice, taken when it was opened, and brought up to date
            // when the invoice changes elsewhere - see refreshPayment.
            $scope.payingInvoice = null;
            $scope.part_payment = { value: 0, account: null, reference_type: null };
            $scope.paymentSaving = false;

            $scope.activeInvoice = null;
            $scope.editor = null;
            $scope.tempForm = resetTempForm();
            $scope.today = $filter("date")(new Date(), "dd-MMMM-yyyy");

            // Xero has to be logged in to, and the patient linked to a contact there, before anything can be
            // listed or raised. step: checking | login | conflict | unreachable | contact | ready
            // `lost` is set when the link went while the dialog was open - see onLinkLost.
            $scope.xero = { step: "checking", organisation: null, contact: null, orgs: null, selectedOrg: null, busy: false, error: null, confirmUnlink: false, lost: null };
            // `selected` is the contact picked from the results, linked with one button as the header's
            // contact list did.
            $scope.contactSearch = { term: "", results: null, selected: null, busy: false, error: null, registerDisabled: false };

            // Adding the patient's email from here, rather than through the header's popover.
            $scope.emailForm = { open: false, value: "", saving: false, error: null };
            pendingCreate = initialAction === "CREATE";

            // The letterhead on the printed invoice.
            $scope.practice = Session.practice || {};

            $scope.$watchCollection("invoices", onInvoicesChanged);
            subscribeToLiveInvoiceChanges();

            checkXero();
        };

        // Another user's invoice changes, applied to this open list as they happen. Only the one
        // changed invoice is fetched, and only when it belongs to the patient on screen - see
        // InvoiceLiveUpdateService.
        //
        // Only $scope.invoices is patched. activeInvoice (the editor) and payingInvoice (the payment form)
        // are the user's own working copies, and must not be moved under them mid-edit.
        function subscribeToLiveInvoiceChanges() {
            InvoiceLiveUpdateService.subscribeInvoiceList($scope, {
                patientId: function () {
                    return $scope.CP.patient && $scope.CP.patient.id;
                },
                invoices: function () {
                    return $scope.invoices;
                },
                onChanged: function () {
                    sortInvoices();
                },
            });

            // The payment card's copy is the one working copy that is moved: a payment against figures that
            // are out of date is refused by the server, so the card follows the invoice instead - see
            // refreshPayment.
            InvoiceLiveUpdateService.watchInvoice($scope, {
                patientId: function () {
                    return $scope.CP.patient && $scope.CP.patient.id;
                },
                invoice: function () {
                    return $scope.selected ? $scope.payingInvoice : null;
                },
                onChanged: refreshPayment,
            });
        }

        // Newest first. Xero's invoice date is a day with no time, so invoices raised on the same day are put
        // in the order they were raised by their invoice number - Xero numbers each new invoice, drafts too,
        // one past the last (INV-0041, INV-0042). Where the number isn't sent, they keep the order they came
        // in, rather than shuffling on every sort.
        function sortInvoices() {
            const position = new Map($scope.invoices.map((invoice, i) => [invoice, i]));
            $scope.invoices.sort(function (a, b) {
                return dayOf(b) - dayOf(a) || numberOf(b) - numberOf(a) || position.get(a) - position.get(b);
            });
        }

        function dayOf(invoice) {
            const time = new Date(invoice.date).getTime();
            return isNaN(time) ? 0 : time;
        }

        // The running number at the end of an invoice number ("INV-0042" is 42), or 0 when there isn't one.
        function numberOf(invoice) {
            const match = /(\d+)\D*$/.exec(invoice.invoice_number || "");
            return match ? Number(match[1]) : 0;
        }

        // ------------------------------------------------------------------
        // Connecting to Xero: login, organisation, the patient's contact
        // ------------------------------------------------------------------

        function patientName() {
            const p = $scope.CP.patient || {};
            return [p.firstName, p.lastName].filter(Boolean).join(" ");
        }

        function checkXero() {
            const xero = $scope.xero;
            xero.step = "checking";
            xero.error = null;
            return $scope.BillingAPI.getLoggedIn()
                .then(function (resp) {
                    const status = resp.data || {};
                    if (status.error_organisation_conflict) {
                        xero.step = "conflict";
                        return loadOrganisations();
                    }
                    if (!status.logged_in_to) {
                        Session.setXeroLoginState(false);
                        xero.step = "login";
                        return;
                    }
                    Session.setXeroLoginState(true);
                    xero.organisation = status.logged_in_to;
                    $scope.accounts = angular.copy(status.accounts) || [];
                    $scope.paymentAccounts = paymentAccountsOf($scope.accounts);
                    $scope.taxRates = angular.copy(status.tax_rates);
                    $scope.providerId = angular.copy(status.tyro_provider_id);
                    return checkContact();
                })
                .catch(function (error) {
                    // The login state is left as it was, as the patient header leaves it: Xero times out
                    // often enough that a failed check is not an answer. Nothing is logged but the status.
                    reportFailure(error);
                    xero.step = error && error.status === 401 ? "login" : "unreachable";
                });
        }

        $scope.checkXero = checkXero;

        // The contact linked to this patient, if there is one; otherwise a search is started for it.
        function checkContact() {
            return $scope.BillingAPI.getContact($scope.CP.patient.id, "").then(function (resp) {
                const linked = (resp.data || []).find((c) => c.linked_to_this_patient) || null;
                $scope.xero.contact = linked;
                $scope.billingContactId = linked ? linked.contact_id : null;
                if (linked) {
                    $scope.xero.lost = null;
                    $scope.xero.step = "ready";
                    onXeroReady();
                } else {
                    $scope.xero.step = "contact";
                    searchInAdvance();
                }
            });
        }

        // Xero refuses an invoice to an archived contact, so none is started for one. Its invoices can
        // still be read and paid.
        $scope.contactArchived = () => !!($scope.xero && $scope.xero.contact && $scope.xero.contact.archived);
        $scope.canInvoice = () => !!$scope.billingContactId && !$scope.contactArchived();

        // What the patient has paid in total is for the practice manager. What is owing and overdue stays
        // for everyone: it is what the front desk collects.
        $scope.canSeePaidTotal = () => Session.hasPermission("BILLING_PAYMENT_TOTAL_VIEW");

        // Once the contact is restored in Xero, looking again lets invoices be raised.
        $scope.recheckContact = function () {
            if ($scope.xero.busy) return;
            $scope.xero.busy = true;
            delete $scope.failed.contact;
            checkContact()
                .catch((error) => fail("contact", error, "Xero couldn't be checked."))
                .finally(() => ($scope.xero.busy = false));
        };

        function onXeroReady() {
            fetchBillingItems();
            fetchInvoices();
            if (heldEditor) {
                restoreEditor();
            } else if (pendingCreate) {
                pendingCreate = false;
                $scope.goToCreate();
            }
        }

        // The patient's link to their Xero contact can go while the dialog is open - disconnected from another
        // screen or by another user, or the contact gone from Xero. Every billing request for the patient is then
        // refused with this, so it is recognised from whichever request meets it first.
        function isLinkLost(error) {
            const message = error && error.data && error.data.message;
            return typeof message === "string" && message.indexOf("not linked with a PAA contact") !== -1;
        }

        // Back to linking the patient, saying why, rather than leaving buttons that can only fail the same way.
        // The contact they were linked to is searched for first, and picked when it is still there, so linking
        // it again is one click. An invoice being written is held, and comes back once they are linked.
        function onLinkLost() {
            // Several requests can meet it together; the first one does this.
            if ($scope.xero.step !== "ready") return;
            const lostContact = $scope.xero.contact;

            if ($scope.viewState !== "LIST" && $scope.editor) {
                delete $scope.editor.confirmDiscard;
                heldEditor = { viewState: $scope.viewState, activeInvoice: $scope.activeInvoice, editor: $scope.editor, tempForm: $scope.tempForm };
            }
            $scope.viewState = "LIST";
            $scope.activeInvoice = null;
            $scope.editor = null;
            $scope.closeInvoice();
            $scope.invoices = [];
            $scope.hasLoaded = false;
            $scope.loadError = null;
            $scope.failed = {};

            $scope.billingContactId = null;
            $scope.xero.contact = null;
            $scope.xero.confirmUnlink = false;
            $scope.xero.lost = { name: lostContact ? lostContact.name : null, keptInvoice: !!heldEditor };
            $scope.xero.step = "contact";

            if (lostContact && lostContact.name) {
                runContactSearch(lostContact.name).then(function (results) {
                    if (!results || !results.length) searchInAdvance();
                });
            } else {
                searchInAdvance();
            }
        }

        function restoreEditor() {
            const held = heldEditor;
            heldEditor = null;
            $scope.activeInvoice = held.activeInvoice;
            $scope.editor = held.editor;
            $scope.tempForm = held.tempForm;
            $scope.viewState = held.viewState;
        }

        // Xero's login opens in its own window; coming back to this one looks again.
        $scope.loginXero = function () {
            $scope.BillingAPI.getXeroLoginUrl()
                .then(function (resp) {
                    $window.open(resp.data, "_blank", "location=no,toolbar=yes,scrollbars=yes,resizable=yes,top=200,left=500,width=500,height=600");
                })
                .catch(function (error) {
                    reportFailure(error);
                    $scope.xero.error = errorMessage(error, "Xero's login page couldn't be opened.");
                });
        };

        function onWindowFocus() {
            if ($scope.xero && $scope.xero.step === "login") $scope.$applyAsync(checkXero);
        }
        $window.addEventListener("focus", onWindowFocus);
        $scope.$on("$destroy", () => $window.removeEventListener("focus", onWindowFocus));

        // More than one Xero organisation is connected for the practice: one is chosen to invoice from.
        function loadOrganisations() {
            $scope.xero.orgs = null;
            $scope.xero.selectedOrg = null;
            return $scope.BillingAPI.getXeroOrg()
                .then(function (resp) {
                    $scope.xero.orgs = angular.copy(resp.data) || [];
                })
                .catch(function (error) {
                    reportFailure(error);
                    $scope.xero.error = errorMessage(error, "The connected Xero organisations couldn't be read.");
                });
        }

        $scope.saveOrganisation = function () {
            if (!$scope.xero.selectedOrg || $scope.xero.busy) return;
            $scope.xero.busy = true;
            $scope.xero.error = null;
            $scope.BillingAPI.setXeroOrg($scope.xero.selectedOrg)
                .then(checkXero)
                .catch(function (error) {
                    reportFailure(error);
                    $scope.xero.error = errorMessage(error, "The organisation couldn't be saved.");
                })
                .finally(() => ($scope.xero.busy = false));
        };

        // First by the exact name a CatTrax-created contact carries (name, DOB and NHI), as the patient
        // header did - a match there means the contact already exists, so creating another is held back.
        // Failing that, by the patient's name.
        function searchInAdvance() {
            const p = $scope.CP.patient;
            const dob = $filter("date")(new Date(p.dateOfBirth), "dd/MM/yyyy");
            const nhi = p.nhi ? `, NHI: ${p.nhi}` : "";
            const exact = `${patientName()} (DOB: ${dob}${nhi})`;

            $scope.contactSearch.registerDisabled = false;
            runContactSearch(exact).then(function (results) {
                if (results && results.length) {
                    $scope.contactSearch.registerDisabled = true;
                } else if (patientName().length > 2) {
                    runContactSearch(patientName());
                }
            });
        }

        function runContactSearch(term) {
            const search = $scope.contactSearch;
            search.term = term;
            search.busy = true;
            search.error = null;
            return $scope.BillingAPI.getContact($scope.CP.patient.id, term)
                .then(function (resp) {
                    search.results = resp.data || [];
                    const linkable = search.results.filter((c) => !c.linked_to_other_patient);
                    search.selected = search.results.length === 1 && linkable.length === 1 ? linkable[0] : null;
                    return search.results;
                })
                .catch(function (error) {
                    reportFailure(error);
                    search.error = "Xero's contacts couldn't be searched.";
                    return null;
                })
                .finally(() => (search.busy = false));
        }

        $scope.findContacts = function () {
            const term = ($scope.contactSearch.term || "").trim();
            if (term.length < 3) {
                $scope.contactSearch.results = null;
                $scope.contactSearch.selected = null;
                return;
            }
            runContactSearch(term);
        };

        $scope.pickContact = function (contact) {
            if (contact.linked_to_other_patient) return;
            $scope.contactSearch.selected = $scope.contactSearch.selected === contact ? null : contact;
        };

        // A contact that is already another patient's is not offered: invoices for this patient would be
        // addressed to them.
        $scope.linkContact = function () {
            const contact = $scope.contactSearch.selected;
            if (!contact || $scope.xero.busy || contact.linked_to_other_patient) return;
            $scope.xero.busy = true;
            $scope.BillingAPI.linkContract($scope.CP.patient.id, contact.contact_id)
                .then(checkContact)
                .catch(function (error) {
                    reportFailure(error);
                    $scope.contactSearch.error = errorMessage(error, "The contact couldn't be linked.");
                })
                .finally(() => ($scope.xero.busy = false));
        };

        // A new Xero contact from the patient's details, linked to them.
        $scope.createContact = function () {
            if ($scope.xero.busy || $scope.contactSearch.registerDisabled) return;
            $scope.xero.busy = true;
            $scope.BillingAPI.registerContact($scope.CP.patient.id)
                .then(checkContact)
                .catch(function (error) {
                    reportFailure(error);
                    $scope.contactSearch.error = errorMessage(error, "The contact couldn't be created in Xero.");
                })
                .finally(() => ($scope.xero.busy = false));
        };

        // Disconnecting the patient from their Xero contact, confirmed in the rail first.
        $scope.unlinkContact = function () {
            const contact = $scope.xero.contact;
            if (!contact || $scope.xero.busy) return;
            $scope.xero.busy = true;
            delete $scope.failed.contact;
            $scope.BillingAPI.unlinkContact($scope.CP.patient.id, contact.contact_id)
                .then(function () {
                    $scope.closeInvoice();
                    $scope.invoices = [];
                    $scope.hasLoaded = false;
                    $scope.xero.contact = null;
                    $scope.xero.confirmUnlink = false;
                    $scope.billingContactId = null;
                    $scope.xero.step = "contact";
                    searchInAdvance();
                })
                .catch((error) => fail("contact", error, "The Xero contact couldn't be disconnected."))
                .finally(() => ($scope.xero.busy = false));
        };

        // ------------------------------------------------------------------
        // The list: status, filters and the balance in the rail
        // ------------------------------------------------------------------

        function emptySummary() {
            return {
                counts: { unpaid: 0, overdue: 0 },
                owing: 0,
                overdue: 0,
                paid: 0,
            };
        }

        function startOfToday() {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            return d;
        }

        function isCancelled(invoice) {
            return invoice.status === "CANCELLED" || invoice.status === "VOIDED" || invoice.status === "DELETED";
        }

        function daysOverdue(invoice, today) {
            if (invoice.status === "PAID" || invoice.status === "DRAFT" || isCancelled(invoice)) return 0;
            if (!(Number(invoice.amount_due) > 0) || !invoice.due_date) return 0;
            const due = new Date(invoice.due_date);
            if (isNaN(due.getTime())) return 0;
            due.setHours(0, 0, 0, 0);
            return Math.max(0, Math.round((today - due) / 86400000));
        }

        function statusOf(invoice, overdueDays) {
            if (invoice.status === "PAID") return { key: "paid", label: "Paid", icon: "fa-check-circle", tone: "ok" };
            if (isCancelled(invoice)) return { key: "cancelled", label: "Cancelled", icon: "fa-ban", tone: "archived" };
            if (invoice.status === "DRAFT") return { key: "draft", label: "Draft", icon: "fa-pencil", tone: "draft" };
            if (overdueDays > 0) return { key: "overdue", label: "Overdue", icon: "fa-exclamation-circle", tone: "out" };
            if (Number(invoice.amount_paid) > 0) return { key: "part", label: "Part paid", icon: "fa-adjust", tone: "low" };
            return { key: "submitted", label: "Submitted", icon: "fa-paper-plane", tone: "low" };
        }

        function dueText(invoice, overdueDays) {
            if (!invoice.due_date) return "";
            const due = $filter("date")(invoice.due_date, "d MMM yyyy");
            if (overdueDays === 1) return "1 day overdue";
            if (overdueDays > 1) return overdueDays + " days overdue";
            return "Due " + due;
        }

        // Each invoice gets a `$view` of what the table shows, worked out once per change rather than per
        // digest. `$` keeps it out of angular.toJson; nothing sends these list rows to the server.
        function onInvoicesChanged() {
            const today = startOfToday();
            const summary = emptySummary();

            ($scope.invoices || []).forEach(function (invoice) {
                const lines = invoice.line_items || [];
                const overdueDays = daysOverdue(invoice, today);
                const status = statusOf(invoice, overdueDays);
                const owing = Number(invoice.amount_due) || 0;

                invoice.$view = {
                    status: status,
                    overdueDays: overdueDays,
                    dueText: dueText(invoice, overdueDays),
                    // What the invoice was for, at a glance in the list: its largest line, the rest counted.
                    primaryItem: primaryItemOf(lines),
                    moreItems: Math.max(0, lines.length - 1),
                    itemsTitle: lines.map((li) => li.description || li.item_code).filter(Boolean).join(", "),
                };

                if (isCancelled(invoice)) return;
                summary.paid += Number(invoice.amount_paid) || 0;
                if (invoice.status !== "PAID" && invoice.status !== "DRAFT" && owing > 0) {
                    summary.counts.unpaid++;
                    summary.owing += owing;
                }
                if (overdueDays > 0) {
                    summary.counts.overdue++;
                    summary.overdue += owing;
                }
            });

            $scope.summary = summary;

            // The reading pane shows the live row, so it follows another user's change as the list does.
            if ($scope.selected) {
                $scope.selected.invoice = findInvoice($scope.selected.invoiceId);
                if (!$scope.selected.invoice && $scope.hasLoaded) $scope.closeInvoice();
            }

            if (pendingInvoiceId && $scope.hasLoaded) {
                const wanted = findInvoice(pendingInvoiceId);
                pendingInvoiceId = null;
                if (wanted) $scope.openInvoice(wanted, true);
            }

            // Nothing open - the first load, or the open invoice deleted - opens the newest one.
            if (!$scope.selected && $scope.hasLoaded && $scope.viewState === "LIST" && $scope.invoices.length) {
                selectInvoice($scope.invoices[0]);
            }
        }

        // ------------------------------------------------------------------
        // Feedback, where the action happened
        // ------------------------------------------------------------------

        // A button's tick, shown for a moment once its action has landed.
        function confirmDone(key) {
            $scope.done[key] = true;
            $timeout(function () {
                delete $scope.done[key];
            }, 2000);
        }

        // An error, shown beside what failed until it is tried again or dismissed. One saying the patient is
        // no longer linked to Xero is not about what failed, and goes back to linking them instead.
        function fail(area, error, fallback) {
            reportFailure(error);
            if (isLinkLost(error)) {
                onLinkLost();
                return;
            }
            $scope.failed[area] = errorMessage(error, fallback);
        }

        // The line an invoice is mostly for - the one with the largest amount, the first of equals.
        function primaryItemOf(lines) {
            const primary = lines.reduce(function (best, line) {
                return !best || (Number(line.line_amount) || 0) > (Number(best.line_amount) || 0) ? line : best;
            }, null);
            return primary ? primary.description || primary.item_code || "" : "";
        }

        $scope.dismissFailure = function (area) {
            delete $scope.failed[area];
        };

        // What changed, highlighted for a moment: an invoice's row in the list, or - from `paymentsFrom` on -
        // the payments on it.
        function markFresh(invoiceId, paymentsFrom) {
            const mark = { invoiceId: invoiceId, paymentsFrom: paymentsFrom === undefined ? null : paymentsFrom };
            $scope.fresh = mark;
            $timeout(function () {
                if ($scope.fresh === mark) $scope.fresh = {};
            }, 2400);
        }

        $scope.isFreshRow = function (invoice) {
            return $scope.fresh.invoiceId === invoice.invoice_id && $scope.fresh.paymentsFrom === null;
        };

        $scope.isFreshPayment = function (index) {
            return !!$scope.selected && $scope.fresh.invoiceId === $scope.selected.invoiceId &&
                $scope.fresh.paymentsFrom !== null && index >= $scope.fresh.paymentsFrom;
        };

        $scope.patientInitials = function () {
            const p = ($scope.CP && $scope.CP.patient) || {};
            return ((p.firstName || "").charAt(0) + (p.lastName || "").charAt(0)).toUpperCase() || "?";
        };

        // ------------------------------------------------------------------
        // Requests
        // ------------------------------------------------------------------

        function findInvoice(invoiceId) {
            return $scope.invoices.find((invoice) => invoice.invoice_id === invoiceId) || null;
        }

        function fetchInvoices() {
            $scope.BillingAPI.isProcessing = true;
            $scope.isLoadingInvoices = true;
            $scope.loadError = null;
            return $scope.BillingAPI.getInvoices($scope.CP.patient.id)
                .then(function (resp) {
                    $scope.invoices = angular.copy(resp.data) || [];
                    sortInvoices();
                    $scope.hasLoaded = true;
                })
                .catch(function (error) {
                    reportFailure(error);
                    if (isLinkLost(error)) {
                        onLinkLost();
                        return;
                    }
                    $scope.loadError = errorMessage(error, "The invoices couldn't be loaded from Xero.");
                })
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                    $scope.isLoadingInvoices = false;
                });
        }

        $scope.refresh = function () {
            if ($scope.isLoadingInvoices) return;
            fetchInvoices().then(function () {
                if ($scope.selected) startPayment();
            });
        };

        function fetchBillingItems() {
            $scope.BillingAPI.isProcessing = true;
            delete $scope.failed.items;
            $scope.BillingAPI.fetchBillingItems()
                .then(function (resp) {
                    $scope.billingItems = angular.copy(resp.data);
                })
                .catch((error) => fail("items", error, "The billing items couldn't be loaded from Xero."))
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                });
        }

        $scope.retryBillingItems = fetchBillingItems;

        // ------------------------------------------------------------------
        // The reading pane: one invoice, and the payment card beside it
        // ------------------------------------------------------------------

        function selectInvoice(invoice) {
            if ($scope.selected && $scope.selected.invoiceId === invoice.invoice_id) return;
            $scope.selected = { invoiceId: invoice.invoice_id, invoice: invoice, confirm: null };
            delete $scope.failed.reader;
            delete $scope.failed.pay;
            startPayment();
        }

        // Opened by the user, from the list or after a save.
        $scope.openInvoice = function (invoice, focusPayment) {
            if (!invoice) return;
            selectInvoice(invoice);
            if (focusPayment) {
                $timeout(function () {
                    const amount = document.getElementById("invPayAmount");
                    if (amount) amount.focus();
                }, 0, false);
            }
        };

        // Kept under its old name: viewing an invoice is now opening it in the reading pane.
        $scope.viewInvoice = function (invoiceId) {
            $scope.openInvoice(findInvoice(invoiceId));
        };

        $scope.payInvoice = function (invoiceId) {
            $scope.openInvoice(findInvoice(invoiceId), true);
        };

        $scope.closeInvoice = function () {
            $scope.selected = null;
            $scope.payingInvoice = null;
        };

        $scope.isSelected = function (invoice) {
            return !!$scope.selected && $scope.selected.invoiceId === invoice.invoice_id;
        };

        $scope.isPayable = function (invoice) {
            return !!invoice && invoice.status !== "PAID" && !isCancelled(invoice) && Number(invoice.amount_due) > 0;
        };

        // Sets the payment bar up on the open invoice as it stands now.
        function startPayment() {
            const invoice = $scope.selected ? findInvoice($scope.selected.invoiceId) : null;
            $scope.payingInvoice = $scope.isPayable(invoice) ? angular.copy(invoice) : null;
            $scope.part_payment = {
                // Defaults to what is still owing rather than 0. Settling an invoice in full is the
                // common case, and starting at 0 meant retyping a figure already on the screen - with
                // a decimal to get wrong, and a part payment recorded if it was. Still editable, and
                // the max on the field and paymentProblem() both still cap it at the amount due.
                value: $scope.payingInvoice ? outstanding($scope.payingInvoice) : 0,
                account: defaultAccount(),
                reference_type: null,
            };
            $scope.payConfirm = false;
        }

        // An amount over what is owing is brought down to it on leaving the field, and one below nothing up
        // to nothing - rather than left for the hint to complain about.
        $scope.clampPayment = function () {
            if (!$scope.payingInvoice || !$scope.part_payment) return;
            const typed = Number($scope.part_payment.value);
            if ($scope.part_payment.value === null || $scope.part_payment.value === undefined || $scope.part_payment.value === "" || isNaN(typed)) return;
            const due = outstanding($scope.payingInvoice);
            if (typed > due) $scope.part_payment.value = due;
            else if (typed < 0) $scope.part_payment.value = 0;
        };

        // Taking a payment is two clicks: Take, then Confirm, with what is about to be recorded spelt out in
        // between - it can't be undone from here. Changing anything on the card goes back to the first step.
        $scope.$watch("part_payment", function (now, before) {
            if (now !== before && !$scope.paymentSaving) $scope.payConfirm = false;
        }, true);

        $scope.requestPayment = function () {
            if (!$scope.isPaymentValid() || $scope.BillingAPI.isProcessing) return;
            $scope.payConfirm = true;
            $timeout(function () {
                const confirm = document.getElementById("invPayConfirm");
                if (confirm) confirm.focus();
            }, 0, false);
        };

        $scope.cancelPayment = function () {
            $scope.payConfirm = false;
        };

        $scope.paymentAccountName = function () {
            const account = ($scope.paymentAccounts || []).find((a) => a.account_id === ($scope.part_payment && $scope.part_payment.account));
            return account ? account.account_name : "";
        };

        function outstanding(invoice) {
            const due = Number(invoice.amount_due);
            return isFinite(due) && due > 0 ? due : 0;
        }

        // The accounts a payment can be put into. Xero takes a payment into any bank account, and into
        // another account only when "Enable payments to this account" is ticked on it there. That flag is
        // not sent to us today, so only bank accounts are offered, as lens dispensing offers them - any
        // other is refused by Xero more often than not. Should the flag be sent (enable_payments_to_account),
        // the accounts it allows are offered too, after the bank accounts.
        function paymentAccountsOf(all) {
            return (all || [])
                .filter(function (account) {
                    return account.account_class === "BANK" || account.enable_payments_to_account === true;
                })
                .sort(function (a, b) {
                    const bankFirst = (a.account_class === "BANK" ? 0 : 1) - (b.account_class === "BANK" ? 0 : 1);
                    return bankFirst || String(a.account_code || a.account_name).localeCompare(String(b.account_code || b.account_name));
                });
        }

        $scope.accountGroup = function (account) {
            return account.account_class === "BANK" ? "Bank accounts" : "Other accounts";
        };

        // The account last paid into at this practice when it can still take a payment, otherwise the first
        // bank account.
        function defaultAccount() {
            if (!$scope.paymentAccounts.length) return null;
            let remembered = null;
            try {
                remembered = window.localStorage.getItem(accountKey());
            } catch (e) {
                remembered = null;
            }
            const chosen = $scope.paymentAccounts.find((a) => a.account_id === remembered) || $scope.paymentAccounts[0];
            return chosen.account_id;
        }

        function rememberAccount(accountId) {
            try {
                if (accountId) window.localStorage.setItem(accountKey(), accountId);
            } catch (e) {
                // Remembering the account is only a convenience.
            }
        }

        $scope.accountLabel = function (account) {
            return (account.account_code ? account.account_code + " - " : "") + account.account_name;
        };

        $scope.methodName = function (value) {
            return ((REFERENCE_TYPE.find((m) => m.value === value) || {}).name || value || "").toLowerCase();
        };

        $scope.payAll = function () {
            if ($scope.payingInvoice) $scope.part_payment.value = outstanding($scope.payingInvoice);
        };

        // What stops the payment, in words, or null when it can be taken.
        $scope.paymentProblem = function () {
            if (!$scope.payingInvoice) return "Nothing is owing on this invoice.";
            const amount = roundToCents(Number($scope.part_payment.value));
            if (!(amount > 0)) return "Enter the amount paid.";
            if (amount > Number($scope.payingInvoice.amount_due)) return "That's more than is owing.";
            if (!$scope.part_payment.reference_type) return "Choose how it was paid.";
            if (!$scope.paymentAccounts.length) return "The Xero organisation has no bank account to take the payment into.";
            if (!$scope.paymentAccounts.some((a) => a.account_id === $scope.part_payment.account)) return "Choose the account it was paid into.";
            return null;
        };

        $scope.isPaymentValid = function () {
            return !$scope.paymentProblem();
        };

        $scope.updatePaidAmount = function () {
            if (!$scope.payConfirm || !$scope.isPaymentValid() || $scope.BillingAPI.isProcessing) return;
            $scope.BillingAPI.isProcessing = true;
            $scope.paymentSaving = true;
            delete $scope.failed.pay;

            const invoiceId = $scope.payingInvoice.invoice_id;
            const paymentsBefore = ($scope.payingInvoice.payments || []).length;
            const amount = roundToCents(Number($scope.part_payment.value));
            const body = {
                amount: amount,
                account_id: $scope.part_payment.account,
                reference_type: $scope.part_payment.reference_type,
                date: $filter("date")(new Date(), "yyyy-MM-dd"),
            };

            $scope.BillingAPI.updatePayment($scope.CP.patient.id, invoiceId, body)
                .then(function () {
                    rememberAccount(body.account_id);
                    return fetchInvoices().then(function () {
                        if (!$scope.selected || $scope.selected.invoiceId !== invoiceId) return;
                        startPayment();
                        confirmDone("pay");
                        markFresh(invoiceId, paymentsBefore);
                    });
                })
                .catch((error) => fail("pay", error, "The payment couldn't be recorded."))
                .finally(function () {
                    $scope.paymentSaving = false;
                    $scope.payConfirm = false;
                    $scope.BillingAPI.isProcessing = false;
                });
        };

        // The open invoice changed elsewhere - paid or part paid by another user or a terminal, or deleted -
        // so the card is brought up to date in place, with nothing for the user to do: what is owing now,
        // keeping the method and account already chosen, and an amount typed by hand unless it is now too
        // much. Paid in full or deleted, the card goes (a deleted invoice also leaves the list).
        //
        // Not while this card's own payment is saving: the save reads the invoice again when it lands.
        function refreshPayment(fresh) {
            if ($scope.paymentSaving || !$scope.payingInvoice) return;
            const previousDue = outstanding($scope.payingInvoice);
            const typed = Number($scope.part_payment.value);

            $scope.payingInvoice = fresh && $scope.isPayable(fresh) ? angular.copy(fresh) : null;
            if (!$scope.payingInvoice) return;

            const due = outstanding($scope.payingInvoice);
            if (!(typed > 0) || typed === previousDue || typed > due) $scope.part_payment.value = due;
        }

        $scope.calculateDiscount = function (invoice) {
            if (!invoice || !invoice.line_items) return 0;
            return invoice.line_items.reduce((acc, current) => {
                const discount = current.discount_rate || 0;
                const price = current.unit_amount || 0;
                const qty = current.quantity || 0;
                return acc + discount * price * qty * 0.01;
            }, 0);
        };

        // ------------------------------------------------------------------
        // Download, send, delete
        // ------------------------------------------------------------------

        function _downloadInvoice(invoiceId) {
            $scope.BillingAPI.isProcessing = true;
            $scope.working.download = true;
            delete $scope.failed.reader;
            $scope.BillingAPI.downloadInvoice($scope.CP.patient.id, invoiceId)
                .then(function (resp) {
                    var blob = new Blob([resp.data], { type: "application/pdf" });
                    var filename = $scope.CP.patient.firstName + "_" + $scope.CP.patient.lastName + "_" + invoiceId;

                    if (typeof window.navigator.msSaveBlob !== "undefined") {
                        window.navigator.msSaveBlob(blob, filename);
                    } else {
                        var tempLink = document.createElement("a");
                        tempLink.href = window.URL.createObjectURL(blob);
                        tempLink.setAttribute("download", filename);
                        document.body.appendChild(tempLink);
                        tempLink.click();
                        document.body.removeChild(tempLink);
                    }
                    confirmDone("download");
                    fetchInvoices();
                })
                .catch((error) => fail("reader", error, "The invoice couldn't be downloaded."))
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                    $scope.working.download = false;
                });
        }

        $scope.downloadInvoice = function (invoiceId) {
            _downloadInvoice(invoiceId);
        };

        function _sendInvoice(invoice) {
            const email = $scope.CP.patient.email;
            $scope.BillingAPI.isProcessing = true;
            $scope.working.send = true;
            delete $scope.failed.reader;
            $scope.BillingAPI.sendInvoice($scope.CP.patient.id, invoice.invoice_id)
                .then(function () {
                    if (email) confirmDone("send");
                    return fetchInvoices();
                })
                .catch((error) => fail("reader", error, "The invoice couldn't be sent."))
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                    $scope.working.send = false;
                    if (!$scope.CP.patient.email) _downloadInvoice(invoice.invoice_id);
                });
        }

        // With no email saved, finalising downloads the invoice instead of sending it - said first, on the
        // open invoice, rather than in a separate dialog.
        $scope.sendInvoice = function (invoice) {
            if (!$scope.CP.patient.email) {
                $scope.openInvoice(invoice);
                $scope.selected.confirm = "send";
                $scope.emailForm = { open: false, value: "", saving: false, error: null };
                focusLater("invSendEmail");
                return;
            }
            _sendInvoice(invoice);
        };

        $scope.confirmSend = function () {
            const invoice = $scope.selected && $scope.selected.invoice;
            $scope.selected.confirm = null;
            if (invoice) _sendInvoice(angular.copy(invoice));
        };

        // ------------------------------------------------------------------
        // The patient's email
        // ------------------------------------------------------------------

        function focusLater(id) {
            $timeout(function () {
                const field = document.getElementById(id);
                if (field) field.focus();
            }, 0, false);
        }

        $scope.openEmailForm = function () {
            $scope.emailForm = { open: true, value: "", saving: false, error: null };
            focusLater("invEmailInput");
        };

        $scope.closeEmailForm = function () {
            $scope.emailForm.open = false;
            $scope.emailForm.error = null;
        };

        // Saved onto the patient as the header's patientEmail component saves it; put back if the save fails.
        // From the send prompt (thenSend), the invoice is then sent to it.
        $scope.saveEmail = function (thenSend) {
            const form = $scope.emailForm;
            const email = (form.value || "").trim();
            if (form.saving) return;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                form.error = "Enter a valid email address.";
                return;
            }
            const previousEmail = $scope.CP.patient.email;
            form.saving = true;
            form.error = null;
            $scope.CP.patient.email = email;
            PatientAPI.isProcessing = true;
            PatientAPI.update($scope.CP.patient)
                .then(function (resp) {
                    $scope.CP.setPatient(resp.data);
                    form.open = false;
                    form.value = "";
                    if (!thenSend) confirmDone("email");
                    if (thenSend && $scope.selected && $scope.selected.confirm === "send") $scope.confirmSend();
                })
                .catch(function (error) {
                    $scope.CP.patient.email = previousEmail;
                    reportFailure(error);
                    form.error = "The email couldn't be saved.";
                })
                .finally(function () {
                    form.saving = false;
                    PatientAPI.isProcessing = false;
                });
        };

        $scope.isCancelDisabled = function (invoice) {
            return !(invoice.status === "DRAFT" || (invoice.status === "SENT" && (invoice.payments || []).length === 0));
        };

        // Its row fades out of the list, and the newest left opens in its place.
        function _cancelInvoice(invoice) {
            $scope.BillingAPI.isProcessing = true;
            $scope.working.delete = true;
            delete $scope.failed.reader;
            $scope.BillingAPI.cancelInvoice($scope.CP.patient.id, invoice.invoice_id)
                .then(function () {
                    if ($scope.selected && $scope.selected.invoiceId === invoice.invoice_id) $scope.closeInvoice();
                    return fetchInvoices();
                })
                .catch(function (error) {
                    if ($scope.selected) $scope.selected.confirm = null;
                    fail("reader", error, "The invoice couldn't be deleted.");
                })
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                    $scope.working.delete = false;
                });
        }

        // Confirmed in place on the open invoice, as the inventory confirms archiving an item.
        $scope.cancelInvoice = function (invoice) {
            if ($scope.isCancelDisabled(invoice)) return;
            $scope.openInvoice(invoice);
            $scope.selected.confirm = "delete";
        };

        $scope.confirmDelete = function () {
            const invoice = $scope.selected && $scope.selected.invoice;
            if (invoice && !$scope.isCancelDisabled(invoice) && !$scope.working.delete) _cancelInvoice(angular.copy(invoice));
        };

        $scope.isEditable = function (invoice) {
            return invoice.status !== "PAID" && (invoice.payments || []).length === 0;
        };

        $scope.transformStatusLabel = function (status) {
            return status === "SENT" ? "SUBMITTED" : status;
        };

        $scope.popoverDownload = { content: "Finalise and download invoice" };
        $scope.popoverPay = { content: "Finalise and take patient payment" };

        $scope.popoverSend = function (email) {
            return {
                content: email ? "Finalise and send invoice" : "Finalise and download invoice. This patient does not have an email address saved. To send an invoice to the patient you must add an email address for this patient before sending",
            };
        };

        // ------------------------------------------------------------------
        // The editor: a new invoice, or changes to one
        // ------------------------------------------------------------------

        function initCreateInvoice() {
            let due_date = new Date();
            due_date.setDate(due_date.getDate() + 14);

            // The reference starts as the patient's name, as every invoice was raised before it could be changed.
            $scope.activeInvoice = {
                date: $scope.today,
                due_date: $filter("date")(due_date, "dd-MMMM-yyyy"),
                reference: `${$scope.CP.patient.firstName} ${$scope.CP.patient.lastName}`,
                line_items: [],
            };
        }

        function openEditor(state, returnTo) {
            $scope.closeInvoice();
            delete $scope.failed.editor;
            $scope.tempForm = resetTempForm();
            $scope.viewState = state;
            $scope.editor = {
                returnTo: returnTo || null,
                snapshot: angular.toJson($scope.activeInvoice),
                confirmDiscard: false,
                closeAfterDiscard: false,
                // What an approved invoice already holds off stock, by variation; a draft holds none.
                held: {},
            };
            lineStock = {};
            loadLineStock($scope.activeInvoice.line_items);
        }

        $scope.goToCreate = function () {
            if (!$scope.canInvoice()) return;
            initCreateInvoice();
            openEditor("CREATE");
        };

        // A new invoice with another's items on it - for a visit billed the same way as the last.
        $scope.duplicateInvoice = function (invoice) {
            if (!$scope.canInvoice()) return;
            initCreateInvoice();
            $scope.activeInvoice.line_items = (invoice.line_items || []).map(function (line) {
                const copy = {
                    item_code: line.item_code,
                    inventory_item_id: line.inventory_item_id,
                    inventory_variation_id: line.inventory_variation_id,
                    description: line.description,
                    quantity: line.quantity,
                    unit_amount: line.unit_amount,
                    tax_type: line.tax_type || "NONE",
                    tax_rate: taxRateOf(line.tax_type),
                    account_code: line.account_code,
                    discount_rate: line.discount_rate || 0,
                };
                $scope.updateValue(copy);
                copy.discount_amount = roundToCents(copy.quantity * copy.unit_amount * copy.discount_rate * 0.01);
                return copy;
            });
            openEditor("CREATE", invoice.invoice_id);
            // Its starting point is the copied items, so leaving it unchanged doesn't ask to discard anything -
            // only once something has been changed on it.
            $scope.editor.isCopy = true;
        };

        $scope.editInvoice = function (invoice, invoiceId) {
            const selected = findInvoice(invoiceId || (invoice && invoice.invoice_id));
            if (!selected || !$scope.isEditable(selected)) return;
            $scope.activeInvoice = angular.copy(selected);
            delete $scope.activeInvoice.$view;
            $scope.activeInvoice.date = $filter("date")(new Date(selected.date), "dd-MMM-yyyy");
            $scope.activeInvoice.due_date = $filter("date")(new Date(selected.due_date), "dd-MMM-yyyy");
            openEditor("EDIT", selected.invoice_id);
            if (selected.status !== "DRAFT") {
                (selected.line_items || []).filter(isInventoryLine).forEach(function (line) {
                    const key = stockKey(line);
                    $scope.editor.held[key] = ($scope.editor.held[key] || 0) + (Number(line.quantity) || 0);
                });
            }
        };

        function editorDirty() {
            if (!$scope.editor || !$scope.activeInvoice) return false;
            if ($scope.hasPendingItem()) return true;
            // A new invoice has work to lose once it has an item; a copy, or an edit, once it differs from how
            // it was opened
            if ($scope.viewState === "CREATE" && !$scope.editor.isCopy) {
                return $scope.activeInvoice.line_items.length > 0;
            }
            return angular.toJson($scope.activeInvoice) !== $scope.editor.snapshot;
        }

        // Back to the list without a request - it is kept current while the editor is open.
        function leaveEditor() {
            const returnTo = $scope.editor && $scope.editor.returnTo;
            $scope.viewState = "LIST";
            $scope.activeInvoice = null;
            $scope.editor = null;
            $scope.tempForm = resetTempForm();
            const back = (returnTo && findInvoice(returnTo)) || $scope.invoices[0];
            if (back) $scope.openInvoice(back);
        }

        // After a save: the list is read again, and the invoice opened to carry on from, its row highlighted.
        function backToList(openId) {
            $scope.viewState = "LIST";
            $scope.activeInvoice = null;
            $scope.editor = null;
            $scope.tempForm = resetTempForm();
            return fetchInvoices().then(function () {
                const invoice = openId ? findInvoice(openId) : null;
                if (invoice) {
                    $scope.openInvoice(invoice);
                    markFresh(openId);
                }
            });
        }

        // The discard card takes the focus, on Keep editing, so Enter or Escape there keeps the work.
        function askToDiscard(closeAfter) {
            $scope.editor.confirmDiscard = true;
            $scope.editor.closeAfterDiscard = closeAfter;
            $timeout(function () {
                const keep = document.getElementById("invKeepEditing");
                if (keep) keep.focus();
            }, 0, false);
        }

        $scope.back = function () {
            if (editorDirty() && !$scope.editor.confirmDiscard) {
                askToDiscard(false);
                return;
            }
            leaveEditor();
        };

        $scope.keepEditing = function () {
            $scope.editor.confirmDiscard = false;
            $scope.editor.closeAfterDiscard = false;
        };

        $scope.discardChanges = function () {
            const close = $scope.editor && $scope.editor.closeAfterDiscard;
            leaveEditor();
            if (close) $scope.$mdDialog.hide();
        };

        $scope.closeModal = function () {
            if ($scope.viewState !== "LIST" && editorDirty()) {
                askToDiscard(true);
                return;
            }
            if ($scope.$mdDialog) {
                $scope.$mdDialog.hide();
            }
        };

        $scope.generateInvoice = function () {
            if (!$scope.canInvoice()) return;

            const body = {
                contact: { contact_id: $scope.billingContactId },
                reference: ($scope.activeInvoice.reference || "").trim(),
                date: $filter("date")(new Date($scope.activeInvoice.date), "yyyy-MM-dd"),
                due_date: $filter("date")(new Date($scope.activeInvoice.due_date), "yyyy-MM-dd"),
                line_items: $scope.activeInvoice.line_items,
            };

            $scope.BillingAPI.isProcessing = true;
            $scope.working.save = true;
            delete $scope.failed.editor;
            $scope.BillingAPI.createInvoice($scope.CP.patient.id, body)
                .then(function (resp) {
                    // Straight on to the new invoice, so payment can be taken there and then - as lens
                    // dispensing goes from creating its invoice to taking payment on it.
                    const created = resp && resp.data && resp.data.invoice_id;
                    return backToList(created);
                })
                .catch((error) => fail("editor", error, "The invoice couldn't be created."))
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                    $scope.working.save = false;
                });
        };

        $scope.updateInvoice = function () {
            const body = {
                line_items: $scope.activeInvoice.line_items,
                invoice_id: $scope.activeInvoice.invoice_id,
                // Sent even when cleared, as the server keeps the reference Xero has whenever this is left out.
                reference: ($scope.activeInvoice.reference || "").trim(),
                due_date: $filter("date")(new Date($scope.activeInvoice.due_date), "yyyy-MM-dd"),
                date: $filter("date")(new Date($scope.activeInvoice.date), "yyyy-MM-dd"),
            };

            $scope.BillingAPI.isProcessing = true;
            $scope.working.save = true;
            delete $scope.failed.editor;
            $scope.BillingAPI.updateInvoice($scope.CP.patient.id, body)
                .then(function () {
                    return backToList(body.invoice_id);
                })
                .catch((error) => fail("editor", error, "The invoice couldn't be saved."))
                .finally(function () {
                    $scope.BillingAPI.isProcessing = false;
                    $scope.working.save = false;
                });
        };

        // The editor's dates are display strings ("15-Oct-2026", "15-October-2026"); this reads either back.
        function parseDisplayDate(value) {
            if (!value) return null;
            const direct = new Date(value);
            if (!isNaN(direct.getTime())) return direct;
            const m = /^(\d{1,2})-([A-Za-z]{3})[A-Za-z]*-(\d{4})$/.exec(value);
            if (!m) return null;
            const month = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].indexOf(m[2].toLowerCase());
            return month === -1 ? null : new Date(Number(m[3]), month, Number(m[1]));
        }

        $scope.setDueIn = function (days) {
            const from = parseDisplayDate($scope.activeInvoice.date) || new Date();
            const due = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
            $scope.activeInvoice.due_date = $filter("date")(due, "dd-MMM-yyyy");
        };

        $scope.duePreset = function (days) {
            if (arguments.length) {
                if (days) $scope.setDueIn(days);
                return days;
            }
            const current = $scope.dueInDays();
            return $scope.DUE_IN_DAYS.indexOf(current) !== -1 ? current : null;
        };

        // Days from the clinic date to the due date, for the preset that matches.
        $scope.dueInDays = function () {
            if (!$scope.activeInvoice) return null;
            const from = parseDisplayDate($scope.activeInvoice.date);
            const due = parseDisplayDate($scope.activeInvoice.due_date);
            if (!from || !due) return null;
            from.setHours(0, 0, 0, 0);
            due.setHours(0, 0, 0, 0);
            return Math.round((due - from) / 86400000);
        };

        function resetTempForm() {
            return { item: null, unit: 1, discount: 0, price: 0, account_code: null, tax_type: null, variations: null, variation: null };
        }

        $scope.onItemSelected = function (item) {
            $scope.tempForm.price = item.unit_price || 0;

            // Automatically map these so they display in the table once added
            $scope.tempForm.account_code = item.account_code || billingDefaults.salesAccountCode;
            $scope.tempForm.tax_type = item.tax_type || billingDefaults.taxType;

            $scope.tempForm.variations = null;
            $scope.tempForm.variation = null;
            if (item.fromInventory) {
                loadVariations(item);
            }
        };

        // An inventory item is sold by its colour and size, which the invoice takes off stock once it is
        // approved (a draft only keeps it); what isn't in stock then goes on backorder for the patient. One
        // variation is picked for the user.
        function loadVariations(item) {
            const form = $scope.tempForm;
            form.loadingVariations = true;
            InventoryAPI.getItem(item.itemId)
                .then(function (resp) {
                    if ($scope.tempForm !== form || form.item !== item) return;
                    // An archived variation - an old colour or size - can no longer be sold.
                    form.variations = InventoryAPI.activeVariations(resp.data);
                    if (form.variations.length === 1) form.variation = form.variations[0];
                })
                .catch(function (error) {
                    // Added without one, the line is invoiced but no stock is taken for it.
                    if ($scope.tempForm === form) form.variations = [];
                    reportFailure(error);
                })
                .finally(() => (form.loadingVariations = false));
        }

        $scope.variationLabel = function (variation) {
            const parts = [variation.color, variation.size].filter(Boolean);
            return parts.length ? parts.join(" / ") : "Standard";
        };

        $scope.variationOption = function (variation) {
            const quantity = variation.quantity || 0;
            return `${$scope.variationLabel(variation)} - ${quantity > 0 ? quantity + " in stock" : "none in stock"}`;
        };

        // How many of the quantity being added aren't in stock, and will go on backorder once the invoice is approved.
        $scope.pendingBackorder = function () {
            const variation = $scope.tempForm && $scope.tempForm.variation;
            if (!variation) return 0;
            return Math.max(0, (Number($scope.tempForm.unit) || 0) - Math.max(0, variation.quantity || 0));
        };

        $scope.needsVariation = function () {
            const form = $scope.tempForm;
            return !!form && !!form.item && !!form.item.fromInventory && (form.loadingVariations || (!!form.variations && form.variations.length > 0 && !form.variation));
        };

        // A billing item picked in the add row but not yet added to the invoice - the easy one to forget.
        $scope.hasPendingItem = function () {
            return !!$scope.tempForm && !!$scope.tempForm.item && typeof $scope.tempForm.item === "object";
        };

        $scope.pendingLabel = function () {
            const item = $scope.hasPendingItem() ? $scope.tempForm.item : null;
            return item ? item.name || item.description || item.code : "";
        };

        // Empties the add row - the other way past an item picked but not added, which holds Create back.
        $scope.clearItem = function (form) {
            if (form) {
                form.$setPristine();
                form.$setUntouched();
            }
            $scope.tempForm = resetTempForm();
            focusLater("invItemSearch");
        };

        $scope.canAddItem = function () {
            return !!$scope.tempForm.item && !!$scope.tempForm.unit && $scope.tempForm.price !== null && $scope.tempForm.price !== undefined
                && Number($scope.tempForm.price) <= $scope.MAX_PRICE && !$scope.needsVariation();
        };

        // A price over the most allowed, on an item already on the invoice, holds the save back until it is
        // brought down (it is, on leaving the field).
        $scope.hasPriceOverMax = function () {
            return !!$scope.activeInvoice && $scope.activeInvoice.line_items.some((line) => Number(line.unit_amount) > $scope.MAX_PRICE);
        };

        function taxRateOf(taxType) {
            if (!taxType || taxType === "NONE" || !$scope.taxRates) return 0;
            const selectedTax = $scope.taxRates.find((t) => t.tax_type === taxType);
            return selectedTax ? parseFloat(selectedTax.tax_display_rate) || 0 : 0;
        }

        $scope.addItem = function (form) {
            if (!$scope.canAddItem()) return;

            const unit_price = $scope.tempForm.price || 0;
            const line_amount = roundToCents($scope.tempForm.unit * unit_price * (100 - $scope.tempForm.discount) * 0.01);
            const discount_amount = roundToCents($scope.tempForm.unit * unit_price * $scope.tempForm.discount * 0.01);
            const tax_type = $scope.tempForm.tax_type || "NONE";
            const variation = $scope.tempForm.item.fromInventory ? $scope.tempForm.variation : null;
            const name = $scope.tempForm.item.description || $scope.tempForm.item.name;
            // The stock the add row read is the line's too, for the warning on it.
            if (variation) rememberStock($scope.tempForm.item.itemId, $scope.tempForm.variations);

            $scope.activeInvoice.line_items.push({
                item_code: $scope.tempForm.item.code,
                // Which colour and size goes on the line, for the patient; the ids are what takes it off stock.
                description: variation && $scope.tempForm.variations.length > 1 ? `${name} - ${$scope.variationLabel(variation)}` : name,
                inventory_item_id: variation ? $scope.tempForm.item.itemId : undefined,
                inventory_variation_id: variation ? variation.variation_id : undefined,
                quantity: $scope.tempForm.unit,
                unit_amount: unit_price,
                tax_type: tax_type,
                tax_rate: taxRateOf(tax_type),
                line_amount: line_amount,
                account_code: $scope.tempForm.account_code,
                discount_rate: $scope.tempForm.discount,
                discount_amount: discount_amount,
            });

            if (form) {
                form.$setPristine();
                form.$setUntouched();
            }
            $scope.tempForm = resetTempForm();
            $timeout(function () {
                const field = document.getElementById("invItemSearch");
                if (field) field.focus();
            }, 0, false);
        };

        // Money is held as binary floating point, so a percentage discount on exact cents produces figures
        // like 89.99100000000001. Rounded to the cent before it is sent, so Xero is given what is shown.
        function roundToCents(amount) {
            return Math.round(amount * 100) / 100;
        }

        $scope.deleteItem = function (index) {
            $scope.activeInvoice.line_items.splice(index, 1);
        };

        $scope.updateValue = function (item) {
            item.line_amount = roundToCents(item.quantity * item.unit_amount * (100 - item.discount_rate) * 0.01);
        };

        // The editor's running figures. GST is left to Xero, which applies it on the practice's settings
        // (prices may include it or not), so it is not guessed at here.
        $scope.linesTotal = function () {
            if (!$scope.activeInvoice) return 0;
            return roundToCents($scope.activeInvoice.line_items.reduce((sum, line) => sum + (Number(line.line_amount) || 0), 0));
        };

        $scope.linesDiscount = function () {
            return roundToCents($scope.calculateDiscount($scope.activeInvoice));
        };

        $scope.loginACC = function () {
            $window.open("https://health.myacc.co.nz/portal/secure/ebusiness/invoicing", "_blank", "location=no,toolbar=yes,scrollbars=yes,resizable=yes,top=200,left=500,width=610,height=656");
        };

        // ------------------------------------------------------------------
        // Stock on the editor's lines: what will go on backorder
        // ------------------------------------------------------------------

        const isInventoryLine = (line) => !!(line && line.inventory_item_id && line.inventory_variation_id);
        const stockKey = (line) => `${line.inventory_item_id}#${line.inventory_variation_id}`;

        function rememberStock(itemId, variations) {
            (variations || []).forEach(function (variation) {
                lineStock[`${itemId}#${variation.variation_id}`] = Math.max(0, variation.quantity || 0);
            });
        }

        // A copied or edited invoice's lines come with their inventory ids, but not the stock. An item that
        // can't be read - archived, or gone - just shows no warning.
        function loadLineStock(lines) {
            if (!inventoryOn()) return;
            const read = lineStock;
            const itemIds = (lines || []).filter(isInventoryLine).map((line) => line.inventory_item_id);
            new Set(itemIds).forEach(function (itemId) {
                InventoryAPI.getItem(itemId)
                    .then(function (resp) {
                        if (lineStock === read) rememberStock(itemId, resp.data && resp.data.variations);
                    })
                    .catch(angular.noop);
            });
        }

        // What the line at this index puts on backorder: lines of one variation share its stock in order,
        // and an approved invoice being changed counts what it already holds as its own. Empty when none.
        $scope.lineBackorderNote = function (index) {
            const lines = ($scope.activeInvoice && $scope.activeInvoice.line_items) || [];
            const line = lines[index];
            if (!isInventoryLine(line)) return "";
            const key = stockKey(line);
            if (!(key in lineStock)) return "";
            const held = ($scope.editor && $scope.editor.held && $scope.editor.held[key]) || 0;
            let before = 0;
            for (let i = 0; i < index; i++) {
                if (isInventoryLine(lines[i]) && stockKey(lines[i]) === key) before += Number(lines[i].quantity) || 0;
            }
            const left = Math.max(0, lineStock[key] + held - before);
            const short = Math.max(0, (Number(line.quantity) || 0) - left);
            if (!short) return "";
            const when = $scope.viewState === "EDIT" && Object.keys($scope.editor.held).length ? "when it's saved" : "when the invoice is approved";
            return `${left > 0 ? "Only " + left + " in stock" : "None in stock"} - ${short} will go on backorder ${when}.`;
        };

        // The practice's inventory is offered alongside its Xero items when it has the feature and the user
        // may read it - otherwise the search would only bring access-denied pop-ups.
        function inventoryOn() {
            const features = Session.practice && Session.practice.practiceFeatures;
            return !!(features && features.inventoryFeature) && Session.hasPermission("PRACTICE_INVENTORY_MANAGE");
        }

        // An inventory item as a billing item. It has no code of its own, so it is given the one it is
        // invoiced under; the server makes the Xero item, named after the line, the first time it is used.
        // The account and tax type fall back to the practice's, as for a Xero item without its own.
        function billingItemFromInventory(item) {
            return {
                code: XeroItemCode.forInventoryItem(item),
                name: item.name,
                unit_price: item.price || 0,
                fromInventory: true,
                itemId: item.item_id,
                categoryLabel: item.category === InventoryAPI.CATEGORY.OTHER && item.other_category
                    ? item.other_category
                    : InventoryAPI.CATEGORY_LABELS[item.category] || "Inventory",
            };
        }

        // The inventory needs 2 characters to search. A failure leaves just the Xero items offered.
        function searchInventory(term) {
            if (!inventoryOn() || term.trim().length < 2) return $q.resolve([]);
            return InventoryAPI.searchItems(term.trim())
                .then((resp) => ((resp.data && resp.data.items) || []).map(billingItemFromInventory))
                .catch(function (error) {
                    reportFailure(error);
                    return [];
                });
        }

        $scope.searchItems = function (typedValue) {
            if (!typedValue) return $q.resolve($scope.billingItems);

            var lowerCaseTerm = typedValue.toLowerCase();

            var xeroItems = $scope.billingItems.filter(function (billingItem) {
                var name = billingItem.name || billingItem.description || "";
                var code = billingItem.code || "";

                return name.toLowerCase().indexOf(lowerCaseTerm) !== -1 ||
                    code.toLowerCase().indexOf(lowerCaseTerm) !== -1;
            });

            // An inventory item already invoiced is in Xero too; it is offered once, from the inventory,
            // at the inventory's price.
            return searchInventory(typedValue).then(function (inventoryItems) {
                const inventoryCodes = inventoryItems.map((item) => item.code);
                return xeroItems.filter((item) => inventoryCodes.indexOf(item.code) === -1).concat(inventoryItems);
            });
        };

        $scope.itemLabel = function (item) {
            if (!item) return "";
            if (item.fromInventory) return item.name + " (" + item.categoryLabel + ")";
            return item.code + " - " + (item.name || item.description);
        };

        $scope.getTaxDisplay = function (taxType) {
            if (!taxType || !$scope.taxRates) return "0% (NONE)";
            const matchedTax = $scope.taxRates.find(t => t.tax_type === taxType);
            if (matchedTax) {
                return parseFloat(matchedTax.tax_display_rate || 0) + "% (" + matchedTax.tax_name + ")";
            }
            return "0% (" + taxType + ")";
        };

        // ------------------------------------------------------------------
        // Errors
        // ------------------------------------------------------------------

        // Only the status is logged - the response carries billing data.
        function reportFailure(error) {
            if (error) console.error("Invoice request failed:", error.status);
        }

        // The server's own reason when it gave one, as lens dispensing shows it; otherwise the fallback.
        function errorMessage(resp, fallback) {
            const message = resp && resp.data && resp.data.message;
            const reason = typeof message === "string" && /CattraxServiceException: (.*?)\]?$/.exec(message);
            if (reason && reason[1]) return reason[1];
            if (resp && resp.status === 403) return "You don't have permission to do that.";
            return fallback;
        }
    }
]);
